"use client";

import React from "react";
import { Calendar, User, Tag, Folder, ArrowLeft } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";

function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="space-y-8 animate-pulse">
        <div className="space-y-4 text-center">
          <div className="h-10 bg-muted rounded-md w-3/4 mx-auto"></div>
          <div className="h-4 bg-muted rounded-md w-1/2 mx-auto"></div>
        </div>
        <div className="h-[400px] bg-muted rounded-xl w-full"></div>
        <div className="space-y-4">
          <div className="h-4 bg-muted rounded-md w-full"></div>
          <div className="h-4 bg-muted rounded-md w-full"></div>
          <div className="h-4 bg-muted rounded-md w-5/6"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive mb-4">Failed to load post content.</p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-default font-bold mb-4 text-foreground">Article Not Found</h2>
        <p className="text-muted-foreground mb-6">The security guide or review you are looking for is currently unavailable.</p>
        <Link to="/HomePage">
          <Button className="bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-lg shadow-sm">
            <ArrowLeft className="mr-2 size-4" />
            Back to Home
          </Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="max-w-4xl mx-auto">
      <header className="mb-10 text-center">
        {post.categories && post.categories.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {post.categories.map((category, i) => (
              <Link key={category.id} to={category.link || "#"} data-index={i}>
                <span className="inline-flex items-center px-3 py-1 rounded-lg bg-destructive/10 text-destructive text-xs font-bold uppercase tracking-wider hover:bg-destructive/20 transition-colors">
                  {category.name}
                </span>
              </Link>
            ))}
          </div>
        )}
        
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-default text-foreground mb-6 leading-tight">
          {post.title}
        </h1>
        
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-muted-foreground text-sm md:text-base">
          {post.author && (
            <div className="flex items-center gap-2">
              <Avatar className="size-6">
                <AvatarImage src={post.author.avatar?.["24"]} alt={post.author.name} />
                <AvatarFallback className="bg-primary text-primary-foreground">{post.author.name.charAt(0)}</AvatarFallback>
              </Avatar>
              <span className="font-medium text-foreground">{post.author.name}</span>
            </div>
          )}
          
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="size-4" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </time>
            </div>
          )}
        </div>
      </header>

      {post.featuredImage && (
        <figure className="mb-12 rounded-2xl overflow-hidden shadow-md">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto max-h-[600px] object-cover"
            loading="eager"
          />
        </figure>
      )}

      <div 
        className="prose prose-lg md:prose-xl dark:prose-invert max-w-none text-foreground prose-headings:font-default prose-headings:font-bold prose-headings:text-primary prose-p:text-muted-foreground prose-a:text-primary prose-a:font-semibold hover:prose-a:text-primary/80 prose-img:rounded-2xl"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />

      <div className="mt-12 pt-8 border-t border-border">
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 mb-8">
            <Tag className="size-5 text-muted-foreground" />
            <span className="text-sm font-bold text-primary uppercase tracking-tight mr-2">Security Topics:</span>
            {post.tags.map((tag, i) => (
              <Link key={tag.id} to={tag.link || "#"} data-index={i}>
                <span className="inline-flex items-center px-3 py-1 rounded-md bg-muted text-muted-foreground text-sm hover:bg-muted/80 transition-colors">
                  {tag.name}
                </span>
              </Link>
            ))}
          </div>
        )}

        {post.author && (
          <Card className="bg-card border border-border shadow-sm rounded-2xl">
            <CardContent className="p-6 sm:p-8 flex flex-col sm:flex-row items-center sm:items-start gap-6">
              <Avatar className="size-20 sm:size-24 border-2 border-background shadow-md">
                <AvatarImage src={post.author.avatar?.["96"]} alt={post.author.name} />
                <AvatarFallback className="text-2xl bg-primary text-primary-foreground">{post.author.name.charAt(0)}</AvatarFallback>
              </Avatar>
              <div className="text-center sm:text-left flex-1">
                <h3 className="text-xl font-bold font-default text-foreground mb-2">
                  Expert Review by {post.author.name}
                </h3>
                {post.author.slug && (
                  <p className="text-muted-foreground mb-4 leading-relaxed">
                    {}
                    A dedicated home safety specialist at SafeBode, focused on helping families make smarter decisions through rigorous product testing and research.
                  </p>
                )}
                <Link to={post.author.link || "#"}>
                  <Button variant="outline" size="sm" className="rounded-lg border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                    View all guides by {post.author.name}
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && window.wvcClient 
    ? window.wvcClient.getCurrentWPQueryParams() 
    : { post_type: "post" }; 
    
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="265040" id="single-post-template-section" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <PostsProvider wp_query={wp_query}>
          <SinglePostConsumer />
        </PostsProvider>
      </div>
    </section>
  );
}